import { useCallback, useEffect, useState } from "react";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ConnectCard, LiveCard } from "@/components/trade/ConnectCards";
import { OrderTicket } from "@/components/trade/OrderTicket";
import { BalancesCard, OrdersTable } from "@/components/trade/OrdersTable";
import { PositionsCard } from "@/components/trade/PositionsCard";
import { AssetPicker, ASSET_KEY, Section, WorkspaceAlerts, WorkspaceChart, WorkspacePredictions, WorkspaceResearch, loadAsset } from "@/components/trade/Workspace";
import { AdvancedSection, BeginnerNote } from "@/components/Beginner";
import { Locked } from "@/components/Locked";
import { useMode } from "@/context/ModeContext";

export default function Trade() {
  const { beginner } = useMode();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("paper");
  const [orders, setOrders] = useState([]);
  const [balances, setBalances] = useState({});
  const [reserved, setReserved] = useState({});
  const [positions, setPositions] = useState(null);
  const [asset, setAsset] = useState(loadAsset);
  const [products, setProducts] = useState(null);
  const pickAsset = (a) => { setAsset(a); try { localStorage.setItem(ASSET_KEY, JSON.stringify(a)); } catch { /* storage blocked: selection still works this session */ } };
  const load = useCallback(async (m = mode) => {
    try {
      const s = (await api.get("/trade/coinbase/status")).data; setStatus(s);
      const useMode = m === "live" && !s.live_enabled ? "paper" : m;
      if (useMode !== m) setMode(useMode);
      const [o, b, p] = await Promise.all([api.get(`/trade/coinbase/orders?mode=${useMode}`), api.get(`/trade/coinbase/balances?mode=${useMode}`),
        useMode === "paper" ? api.get("/trade/coinbase/positions?mode=paper") : Promise.resolve({ data: null })]);
      setOrders(o.data.orders); setBalances(b.data.balances); setReserved(b.data.reserved || {}); setPositions(p.data);
    } catch (err) { toast.error(apiError(err)); }
  }, [mode]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => load(), 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get("/trade/coinbase/products").then((r) => setProducts(r.data.products)).catch(() => setProducts([])); }, []);
  if (!status) return <div className="p-6 flex items-center gap-2 text-slate-400 text-sm" data-testid="cb-trade-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading paper trading…</div>;

  const productId = (products?.find((p) => p.base === asset.symbol && p.quote === "USD") || products?.find((p) => p.base === asset.symbol))?.product_id || null;
  const scrollToTicket = () => document.getElementById("paper-ticket")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="cb-trade-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><ArrowLeftRight className="w-7 h-7 text-cyan-300" /> Paper Trading</h1>
        <p className="text-sm text-slate-400 mt-1">Pick a crypto and everything you need is on this one page: its live chart, the paper Buy/Sell ticket, EV-IDEX's research and its sealed prediction record. <strong className="text-slate-200">Paper mode is the default</strong>; live orders require your own exchange API key, an explicit opt-in and a confirmation for every order.</p>
        <p className="text-[11px] text-slate-500 mt-1" data-testid="cb-disclaimer">{status.disclaimer}</p>
      </div>

      <AssetPicker asset={asset} onPick={pickAsset} />

      <div className="flex items-center gap-2" data-testid="cb-mode-switch">
        {["paper", "live"].map((m) => <button key={m} type="button" data-testid={`cb-mode-${m}`} disabled={m === "live" && !status.live_enabled} onClick={() => { setMode(m); load(m); }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${mode === m ? (m === "live" ? "bg-rose-500/20 border-rose-700 text-rose-200" : "bg-cyan-500/15 border-cyan-700 text-cyan-200") : "border-slate-800 text-slate-400 disabled:opacity-40"}`}>{m === "paper" ? "Paper (simulated)" : "Live (real funds)"}</button>)}
        {!status.live_enabled && <span className="text-[11px] text-slate-500">Live mode unlocks after you connect a key and enable live trading below.</span>}
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-4 items-start">
        <div className="space-y-4">
          <Section step="1" title={`${asset.name} · live chart`} hint="real market price, updating as trades print" tid="trade-section-chart">
            <WorkspaceChart asset={asset} />
            <div className="border-t border-slate-800/70 pt-3">
              <Locked feature="alerts" compact title="Price alerts are a Pro feature" desc={`Set "notify me when ${asset.symbol} goes above / below a price" right here, delivered in-app and as push. Your alerts unlock instantly on Pro.`} testid="trade-alerts-locked">
                <WorkspaceAlerts asset={asset} />
              </Locked>
            </div>
          </Section>

          <Section step="2" title="Paper Buy / Sell" hint="simulated funds, real prices" tid="trade-section-ticket">
            <div id="paper-ticket" className="scroll-mt-24" />
            <BeginnerNote testid="trade-ticket-note"><strong className="text-slate-300">How to place a practice trade</strong> — choose BUY or SELL, how much, then Preview. Market = fill now at the current price; Limit = only at your price or better; Stop-limit = becomes a limit order once the trigger price is hit. Optional stop-loss / take-profit close the position for you later. Nothing here uses real money.</BeginnerNote>
            {products && !productId && <div className="rounded-lg border border-amber-900/60 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" data-testid="trade-no-product">No paper market for {asset.symbol} yet — paper trading covers assets with a supported USD pair. You can still read the chart, research and predictions above and below.</div>}
            <OrderTicket mode={mode} status={status} onPlaced={() => load()} productId={productId || undefined} />
          </Section>

          <Section step="3" title={`Research · ${asset.symbol}`} hint="EV-IDEX's plain-English read" tid="trade-section-research">
            <WorkspaceResearch asset={asset} onPaperTrade={scrollToTicket} />
          </Section>

          <Section step="4" title={`Predictions · ${asset.symbol}`} hint="sealed record of past calls" tid="trade-section-predictions">
            <WorkspacePredictions asset={asset} />
          </Section>

          <Section step="5" title="Your paper positions & orders" tid="trade-section-account">
            {mode === "paper" && <PositionsCard data={positions} />}
            <OrdersTable orders={orders} reload={() => load()} />
          </Section>
        </div>

        <div className="space-y-4">
          <BalancesCard balances={balances} reserved={reserved} mode={mode} start={status.paper_start_usd} />
          {beginner ? (
            <AdvancedSection title="Exchange connection & live trading" testid="trade-advanced-connect" what="Connect your own exchange API key and opt in to live trading." why="Only needed if you ever want to leave paper mode. Live trading stays off until you explicitly enable it.">
              <div className="space-y-4"><ConnectCard status={status} reload={() => load()} /><LiveCard status={status} reload={() => load()} /></div>
            </AdvancedSection>
          ) : (
            <><ConnectCard status={status} reload={() => load()} /><LiveCard status={status} reload={() => load()} /></>
          )}
          <div className="text-[11px] text-slate-500 apx-card p-3" data-testid="cb-fee-note">Fees in paper mode are estimates ({status.fee_estimate.taker_pct}% taker / {status.fee_estimate.maker_pct}% maker). {status.fee_estimate.note}</div>
        </div>
      </div>
    </div>
  );
}
